import { memo } from 'react';
import { LeadAvatar } from '@/components/leads/LeadAvatar';
import { SourceBadge } from '@/components/leads/SourceBadge';
import { TemperatureBadge } from '@/components/leads/TemperatureBadge';
import type { IBoardCardPreviewProps } from '@/@types/leadsBoard';

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

/**
 * Non-interactive card visual — no `useDraggable`, no `<Link>`, no
 * `onClick`/`tabIndex`. Reused in two places so the two markups can't drift:
 * dnd-kit's <DragOverlay> renders it as-is (no `nameSlot`), and BoardCard
 * wraps it with drag/click behavior and supplies the real navigable Link via
 * `nameSlot`. A real BoardCard rendered inside DragOverlay would duplicate a
 * `role="button" tabIndex={0}` node with the same accessible name as the card
 * being dragged, plus a duplicate nested link.
 */
export const BoardCardPreview = memo(function BoardCardPreview({
  lead,
  nameSlot,
}: IBoardCardPreviewProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2">
      <div className="flex items-center gap-2">
        <LeadAvatar name={lead.name} profilePicture={lead.profilePicture} size="sm" />
        <div className="min-w-0 flex-1">
          {nameSlot ?? (
            <div className="font-medium text-sm text-foreground truncate">{lead.name}</div>
          )}
          <div className="text-xs text-muted-foreground">
            {formatRelativeTime(lead.lastMessageAt)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <SourceBadge source={lead.source} />
        {lead.temperature && <TemperatureBadge temperature={lead.temperature} size="sm" />}
      </div>
    </div>
  );
});
